/**
 * CompetitionCard — used in the Live Competitions tab.
 * Renders teaser variant when competition.locked=true.
 */
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Lock, Clock, Crown, Award, Trophy, Camera, Users, Gift, Sparkles } from 'lucide-react';
import FisheryImage from './FisheryImage';
import { trackTierGate } from '../lib/tierGateTracker';

const ICONS = {
  catch: Trophy,
  photo: Camera,
  community: Users,
  giveaway: Gift,
  league: Award,
};

const TIER_STYLES = {
  basic: { label: 'Open to all', cls: 'bg-[#1f221f] text-[#8a948a]' },
  pro: { label: 'Pro only', cls: 'bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30' },
  elite: { label: 'Elite exclusive', cls: 'bg-gradient-to-r from-[#c8b273]/30 to-[#7d6730]/30 text-[#c8b273] border border-[#c8b273]/60' },
};

const formatRemaining = (endsAt) => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return 'Ended';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return `${d}d ${h}h left`;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
};

const isEndingSoon = (endsAt) => {
  if (!endsAt) return false;
  const ms = new Date(endsAt).getTime() - Date.now();
  return ms > 0 && ms < 48 * 3600 * 1000;
};

const CompetitionCard = ({ competition: c, onView }) => {
  const [remaining, setRemaining] = useState(formatRemaining(c.ends_at));
  useEffect(() => {
    const id = setInterval(() => setRemaining(formatRemaining(c.ends_at)), 60_000);
    return () => clearInterval(id);
  }, [c.ends_at]);

  const Icon = ICONS[c.competition_type] || Trophy;
  const tierStyle = TIER_STYLES[c.tier_required] || TIER_STYLES.basic;
  const ending = isEndingSoon(c.ends_at);

  return (
    <div
      data-testid={`competition-card-${c.slug || c.id}`}
      className={`relative glass-card overflow-hidden transition-all hover:border-[#c8b273]/40 ${
        c.is_pinned ? 'ring-1 ring-[#c8b273]/40' : ''
      }`}
    >
      {/* Banner area / atmospheric fishery imagery */}
      <FisheryImage
        src={c.banner_image}
        seed={c.slug || c.id}
        alt={c.title}
        aspect="none"
        className="h-32"
        imgClassName="opacity-70"
        testId={`competition-banner-${c.slug || c.id}`}
      >
        <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#c8b273]/10 blur-3xl z-10" />
        <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#c8b273]/15 border border-[#c8b273]/30 backdrop-blur">
            <Icon className="h-4 w-4 text-[#c8b273]" />
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${tierStyle.cls}`}>
            {tierStyle.label}
          </span>
        </div>
        {c.is_pinned && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#c8b273] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded z-10">
            <Sparkles className="h-3 w-3" /> Featured
          </span>
        )}
        {ending && !c.locked && !c.launching_soon && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-red-500/20 text-red-300 border border-red-500/30 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded z-10">
            <Clock className="h-3 w-3" /> Ending Soon
          </span>
        )}
        {c.launching_soon && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded z-10 backdrop-blur">
            <Lock className="h-3 w-3" /> Launching Soon
          </span>
        )}
      </FisheryImage>

      <div className="p-5">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-2">
          {c.title}
        </h3>
        <p className="text-sm text-[#8a948a] mb-4 line-clamp-2">
          {c.description}
        </p>

        {/* Prize line — hides the £-value during soft launch */}
        <div className="flex items-start gap-2 text-sm mb-4">
          <Trophy className="h-4 w-4 text-[#c8b273] mt-0.5 flex-shrink-0" />
          <span className="text-[#e2e4e2]">{c.prize_summary || 'Prize details to follow'}</span>
        </div>

        {/* Countdown + CTA */}
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-xs text-[#8a948a]">
            <Clock className="h-3.5 w-3.5" />
            {c.launching_soon ? 'Founding Season' : (remaining || '—')}
          </span>
          {c.locked ? (
            <Link
              to="/subscription"
              data-testid={`comp-upgrade-${c.slug || c.id}`}
              onClick={() => trackTierGate({
                surface: 'competition_locked',
                feature: `competition.${c.competition_type || 'unknown'}`,
                requiredTier: c.tier_required,
                metadata: { slug: c.slug, id: c.id, title: c.title },
              })}
              className="inline-flex items-center gap-1 rounded bg-[#c8b273] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-[#d8c283]"
            >
              <Crown className="h-3.5 w-3.5" />
              Upgrade
            </Link>
          ) : c.launching_soon ? (
            <span
              data-testid={`comp-launching-${c.slug || c.id}`}
              className="inline-flex items-center gap-1 rounded border border-[#c8b273]/40 bg-[#c8b273]/5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#c8b273]"
            >
              <Lock className="h-3.5 w-3.5" />
              Soon
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onView?.(c)}
              data-testid={`comp-view-${c.slug || c.id}`}
              className="inline-flex items-center gap-1 rounded border border-[#c8b273]/40 bg-transparent px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#c8b273] transition-colors hover:bg-[#c8b273] hover:text-black"
            >
              View
            </button>
          )}
        </div>

        {/* Locked teaser overlay */}
        {c.locked && (
          <div className="mt-3 p-3 rounded border border-[#c8b273]/30 bg-[#c8b273]/5 flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#c8b273] flex-shrink-0" />
            <p className="text-xs text-[#c8b273]">
              {c.tier_required === 'elite' ? 'Elite-exclusive competition' : 'Pro members only'}
            </p>
          </div>
        )}
        {c.launching_soon && !c.locked && (
          <div className="mt-3 p-3 rounded border border-[#c8b273]/25 bg-[#c8b273]/5 flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#c8b273] flex-shrink-0" />
            <p className="text-xs text-[#c8b273]">Early members get priority access when this drops.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionCard;
